import { CHOSEN_MODELS_LABEL } from "@shared/types/transformations";
import { dbtModelCountsOf } from "@shared/types/dbtModelCounts";
import { PARTLY_FAILED_LABEL, type PartlyFailedFacts } from "@shared/runs/partlyFailed";
import {
  NO_ROWS_HOVER,
  NO_ROWS_LABEL,
  NOT_CREATED_HOVER,
  NOT_CREATED_LABEL,
  tableNoRows,
} from "@shared/runs/noRows";
import { formatInstant } from "@/lib/time/displayTime";
import type { SkyBadgeColor } from "@skypointcloud/skypoint-ui";
import type {
  EntityProgressRow,
  RunKind,
  RunStatus,
  RunStepRow,
  RunSummaryRow,
  RunTotals,
  RunTransformTotals,
  RunType,
  StepName,
  StepStatus,
} from "@shared/types/runs";

/**
 * How a run reads on screen: durations and the colour and word for every status
 * (FR-56, UX-5). An instant renders through `formatInstant` (`lib/time/displayTime.ts`) in
 * the reader's display zone.
 *
 * One module for all of it because the runs list, the run detail stepper and
 * the entity grid must agree. Two copies of "what colour is `partially_
 * succeeded`" is how a row ends up green on one screen and amber on the next,
 * and colour is never the only signal here: every badge carries its word too,
 * which is what WCAG 1.4.1 asks for and what a colour-blind operator reads.
 */

/** `3m 12s`, `41s`, `1h 04m`. Null durations read as an em dash, never as `0s`. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return "—";
  const total = Math.max(0, Math.round(seconds));
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}m ${String(total % 60).padStart(2, "0")}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${String(minutes % 60).padStart(2, "0")}m`;
}

/**
 * One table's stage time: tenths under a minute (`7.1s`, `1s`), as the server reports it, so two
 * short statements that differ by less than a second still read apart. A minute or more reads
 * like `formatDuration`.
 */
export function formatStageSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return "—";
  const tenths = Math.round(Math.max(0, seconds) * 10) / 10;
  return tenths < 60 ? `${tenths}s` : formatDuration(seconds);
}

export interface StatusPresentation {
  label: string;
  color: SkyBadgeColor;
  /** True while the row is expected to keep changing. */
  isActive: boolean;
  /** The badge's hover, when it needs words of its own (#2392). */
  title?: string;
}

const RUN_STATUS_PRESENTATION: Record<RunStatus, StatusPresentation> = {
  queued: { label: "Queued", color: "informative", isActive: true },
  running: { label: "Running", color: "informative", isActive: true },
  succeeded: { label: "Succeeded", color: "success", isActive: false },
  // #817: what failed follows the word ("Partly failed: 1 of 2 connections failed (HR)",
  // `runPartlyFailedFacts`), so it agrees with Connections, which reads that connection Failed.
  partially_succeeded: { label: PARTLY_FAILED_LABEL, color: "warning", isActive: false },
  failed: { label: "Failed", color: "danger", isActive: false },
  cancelled: { label: "Cancelled", color: "subtle", isActive: false },
};

export function runStatusPresentation(status: RunStatus): StatusPresentation {
  return RUN_STATUS_PRESENTATION[status] ?? { label: status, color: "subtle", isActive: false };
}

/**
 * #817: the facts `shared/runs/partlyFailed.ts` words a partly failed run from, the same on the
 * Runs list and the run page. With the run's step rows (the run page) the failed connections and
 * a failed transform are read from them; from a list row, from `failedDataflowIds` and
 * `transformFailed`. A name the map lacks reads as the id, as "What ran" does.
 */
export function runPartlyFailedFacts(
  run: Pick<
    RunSummaryRow,
    "dataflowIds" | "failedDataflowIds" | "partlyLoadedDataflowIds" | "transformFailed" | "totals"
  >,
  connectionNames: ReadonlyMap<string, string> = new Map(),
  steps?: ReadonlyArray<Pick<RunStepRow, "step" | "status" | "dataflowId" | "dataflowName">>,
): PartlyFailedFacts {
  const tables = {
    tables: run.totals?.tables ?? null,
    tablesNotLoaded: run.totals?.tablesFailed ?? null,
  };
  if (steps) {
    const failed = new Map<string, string>();
    const partly = new Map<string, string>();
    const partlyIds = new Set(run.partlyLoadedDataflowIds ?? []);
    for (const row of steps) {
      if (row.status !== "failed" || row.dataflowId === null) continue;
      if (failed.has(row.dataflowId) || partly.has(row.dataflowId)) continue;
      (partlyIds.has(row.dataflowId) ? partly : failed).set(
        row.dataflowId,
        row.dataflowName ?? connectionNames.get(row.dataflowId) ?? row.dataflowId,
      );
    }
    return {
      connections: run.dataflowIds.length,
      failedConnectionNames: [...failed.values()],
      partlyLoadedConnectionNames: [...partly.values()],
      transformFailed: steps.some((row) => row.step === "transform" && row.status === "failed"),
      ...tables,
    };
  }
  const partlyIds = new Set(run.partlyLoadedDataflowIds ?? []);
  return {
    connections: run.dataflowIds.length,
    failedConnectionNames: (run.failedDataflowIds ?? [])
      .filter((id) => !partlyIds.has(id))
      .map((id) => connectionNames.get(id) ?? id),
    partlyLoadedConnectionNames: (run.failedDataflowIds ?? [])
      .filter((id) => partlyIds.has(id))
      .map((id) => connectionNames.get(id) ?? id),
    transformFailed: run.transformFailed === true,
    ...tables,
  };
}

/** True when the run can no longer change on its own (FR-54's cancel guard). */
export function isTerminalRun(status: RunStatus): boolean {
  return !runStatusPresentation(status).isActive;
}

const STEP_STATUS_PRESENTATION: Record<StepStatus, StatusPresentation> = {
  pending: { label: "Waiting", color: "subtle", isActive: false },
  running: { label: "Running", color: "informative", isActive: true },
  succeeded: { label: "Succeeded", color: "success", isActive: false },
  failed: { label: "Failed", color: "danger", isActive: false },
  skipped: { label: "Skipped", color: "warning", isActive: false },
  cancelled: { label: "Cancelled", color: "subtle", isActive: false },
};

export function stepStatusPresentation(status: StepStatus): StatusPresentation {
  return STEP_STATUS_PRESENTATION[status] ?? { label: status, color: "subtle", isActive: false };
}

/**
 * The status word a step shows on screen: its presentation's label, except a step still pending
 * in a finished run, which will never start and reads "Not run". The one rule for the step's
 * status line, its badge in the step details and (#1089) its accessible name in the stepper.
 */
export function stepStatusWord(status: StepStatus, terminalRun: boolean): string {
  if (status === "pending" && terminalRun) return "Not run";
  return stepStatusPresentation(status).label;
}

/**
 * The heading over a step's message (#853): a skipped step "was skipped", a cancelled one
 * "cancelled" (an operator's cancel, or a disabled tenant, is never a failure; the run list and
 * the reliability figures already read it as Cancelled), and any other step with an error failed.
 */
export function stepMessageTitle(step: StepName, status: StepStatus, name?: string): string {
  // #1202: the heading's own name for the step when the caller has it ("Finish the run" on a
  // transformation run), else the finishing step is "Finish the import".
  const label = name ?? (step === "finalize" ? "Finish the import" : (STEP_LABELS[step] ?? step));
  if (status === "skipped") return `${label} was skipped`;
  if (status === "cancelled") return `${label} cancelled`;
  return `${label} failed`;
}

/** The message bar's intent for a step's message: only a failed step reads as an error. */
export function stepMessageIntent(status: StepStatus): "warning" | "info" | "error" {
  if (status === "skipped") return "warning";
  if (status === "cancelled") return "info";
  return "error";
}

/** Title case for the stepper: `extract` → `Extract`. */
export const STEP_LABELS: Record<StepName, string> = {
  extract: "Extract",
  load: "Load",
  merge: "Merge",
  transform: "Transform",
  finalize: "Finalize",
};

/** A count a person can scan: `1,204,883`. */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return "—";
  return value.toLocaleString();
}

/* ------------------------------------------------------------------------------------- *
 * Rows per table (Session 7, O1)
 * ------------------------------------------------------------------------------------- */

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${formatCount(n)} ${n === 1 ? one : many}`;
}

/** One part of the rows sentence; only a failure part is `danger`. */
export interface RowsLinePart {
  text: string;
  tone: "default" | "danger";
}

/**
 * True when no extract count stands behind `rowsExtracted`: it is zero, yet the load read rows
 * (loaded, or expected from the extract's manifest). That is the run whose tables only the load
 * reported. `RunTotals` carries no per-table flag, so the list, which sees only totals, can tell
 * no more than this; a run whose load-only tables all failed before a count still reads 0.
 */
export function extractUnreported(totals: RunTotals): boolean {
  return (
    totals.rowsExtracted === 0 && ((totals.rowsLoaded ?? 0) > 0 || (totals.expectedRows ?? 0) > 0)
  );
}

/**
 * The parts of the one rows sentence the run page, the runs list and the connection's last-run
 * card all show, so the three can never disagree. A part whose number nobody reported drops out
 * rather than reading as zero.
 *
 * - long: `2 tables · 1,000 rows extracted · 996 of 1,000 rows loaded · 1 table failed to load · 2m 31s`
 * - short: `1,000 extracted · 996 loaded`
 *
 * - running, with tables still to start (`plannedTables`, #1127): `4 of 7 tables started · ...`
 *
 * When only the load reported the run's tables (every row `extractReported: false`), the extract
 * count reads as the grid's dash and "not reported", never "0 extracted"; see `extractUnreported`.
 */
export function rowsLineParts(
  totals: RunTotals | undefined,
  opts: { durationSeconds?: number | null; short?: boolean; plannedTables?: number } = {},
): RowsLinePart[] {
  // #1127: while the run moves, the long line counts its tables against what the extract set out
  // to read ("4 of 7 tables started"), so it never reads as a smaller total than the banner's.
  const planned = opts.plannedTables ?? 0;
  if (!totals) {
    return planned > 0 && !opts.short
      ? [{ text: `0 of ${plural(planned, "table")} started`, tone: "default" }]
      : [{ text: "—", tone: "default" }];
  }
  const parts: RowsLinePart[] = [];
  const add = (text: string, tone: RowsLinePart["tone"] = "default") => parts.push({ text, tone });

  if (opts.short) {
    add(
      extractUnreported(totals)
        ? "extracted count unknown"
        : `${formatCount(totals.rowsExtracted)} extracted`,
    );
    if (totals.rowsLoaded !== null) add(`${formatCount(totals.rowsLoaded)} loaded`);
    return parts;
  }

  add(
    planned > totals.tables
      ? `${formatCount(totals.tables)} of ${plural(planned, "table")} started`
      : plural(totals.tables, "table"),
  );
  add(
    extractUnreported(totals)
      ? "rows extracted not reported"
      : `${plural(totals.rowsExtracted, "row")} extracted`,
  );
  if (totals.rowsLoaded !== null) {
    add(
      totals.expectedRows !== null && totals.expectedRows !== totals.rowsLoaded
        ? `${formatCount(totals.rowsLoaded)} of ${plural(totals.expectedRows, "row")} loaded`
        : `${plural(totals.rowsLoaded, "row")} loaded`,
    );
  }
  if (totals.tablesLoadFailed > 0) {
    add(`${plural(totals.tablesLoadFailed, "table")} failed to load`, "danger");
  }
  // A table counts once in `tablesFailed`; the ones whose load did not fail failed to extract.
  const extractFailed = totals.tablesFailed - totals.tablesLoadFailed;
  if (extractFailed > 0) add(`${plural(extractFailed, "table")} failed to extract`, "danger");
  if (opts.durationSeconds !== null && opts.durationSeconds !== undefined) {
    add(formatDuration(opts.durationSeconds));
  }
  return parts;
}

/* ------------------------------------------------------------------------------------- *
 * What ran (Session 10, B13)
 * ------------------------------------------------------------------------------------- */

/** A run's trigger, as the Trigger column and the filter chip say it. */
export const RUN_KIND_LABELS: Record<RunKind, string> = {
  scheduled: "Scheduled",
  on_demand: "Manual",
  retry: "Retry",
  event_drain: "Event drain",
};

/**
 * Round 4 (owner decision 10b, 2026-09-30): the kind of start, as line 1 of the Started by cell
 * on the Runs list and a connection's Runs. A firing is "Scheduler" there (who started it), not
 * the Trigger filter's "Scheduled" (how it was triggered).
 */
export const STARTED_BY_KIND_LABELS: Record<RunKind, string> = {
  ...RUN_KIND_LABELS,
  scheduled: "Scheduler",
};

/** The run's Type badge. */
export const RUN_TYPE_LABELS: Record<RunType, string> = {
  import: "Import",
  transform: "Transform",
  import_transform: "Import + transform",
};

/** What a run with no tables and no reported model counts says in the Rows column. */
export const NO_TABLES_TRANSFORM_ONLY = "No tables (transform only)";

/**
 * The transform step's short line: `4 models · 23 tests passed`, with failures as their own
 * `danger` parts (`1 model failed`, `2 tests failed`). A count nobody reported drops out, so the
 * list is empty until the step reports something. The caller picks the empty case: a
 * transform-only run reads `No tables (transform only)`, and an import + transform run shows no
 * transform line at all (its step is created up front with no counts).
 */
export function transformLineParts(totals: RunTransformTotals | undefined): RowsLinePart[] {
  const parts: RowsLinePart[] = [];
  // Round 6, T5: a step that recorded the four counts says built of selected, then failed and
  // skipped; a model dbt skipped because its upstream failed is never counted as built.
  const counts = totals ? dbtModelCountsOf(totals) : null;
  if (counts?.exact) {
    parts.push({ text: `${counts.built} of ${counts.selected} models built`, tone: "default" });
    if (counts.failed > 0)
      parts.push({ text: `${formatCount(counts.failed)} failed`, tone: "danger" });
    if (counts.skipped > 0) {
      parts.push({ text: `${formatCount(counts.skipped)} skipped`, tone: "default" });
    }
  } else if (totals?.modelsReported !== null && totals?.modelsReported !== undefined) {
    parts.push({ text: plural(totals.modelsReported, "model"), tone: "default" });
  }
  if (!counts?.exact && totals?.modelsFailed) {
    parts.push({
      text: `${plural(totals.modelsFailed, "model")} failed`,
      tone: "danger",
    });
  }
  if (
    totals?.testsSucceeded !== null &&
    totals?.testsSucceeded !== undefined &&
    totals.testsTotal
  ) {
    parts.push({ text: `${plural(totals.testsSucceeded, "test")} passed`, tone: "default" });
  }
  if (totals?.testsFailed) {
    parts.push({ text: `${plural(totals.testsFailed, "test")} failed`, tone: "danger" });
  }
  return parts;
}

/**
 * Round 6 T-29: `dbt job · staging`, `dbt jobs · staging, marts`, `Transform · Chosen models`
 * for a run of chosen models (no dbt job ran), or `Transform` before the step recorded what it ran.
 */
export function transformWhatRan(names: readonly string[] | undefined): string {
  if (!names || names.length === 0) return "Transform";
  if (names.length === 1 && names[0] === CHOSEN_MODELS_LABEL) return `Transform · ${names[0]}`;
  return `${names.length === 1 ? "dbt job" : "dbt jobs"} · ${names.join(", ")}`;
}

/** The run's connections as one short phrase: `Orders DB`, `Orders DB and 2 more`. */
export function connectionsWhatRan(
  dataflowIds: readonly string[],
  connectionNames: ReadonlyMap<string, string>,
): string | null {
  const names = dataflowIds.map((id) => connectionNames.get(id) ?? id);
  if (names.length === 0) return null;
  return names.length === 1 ? (names[0] as string) : `${names[0]} and ${names.length - 1} more`;
}

/** The rows sentence as plain text; `—` when the run has no tables. */
export function formatRowsLine(
  totals: RunTotals | undefined,
  opts: { durationSeconds?: number | null; short?: boolean } = {},
): string {
  return rowsLineParts(totals, opts)
    .map((part) => part.text)
    .join(" · ");
}

/**
 * The run's totals from its table rows: the client twin of the server's `totalsFromEntities`,
 * for a response from a server that predates `totals`. Undefined when there are no rows.
 */
export function totalsFromRows(all: readonly EntityProgressRow[]): RunTotals | undefined {
  // #1127: a planned table that has not reported is not a table the run has figures for.
  const rows = all.filter((row) => row.status !== "waiting");
  if (rows.length === 0) return undefined;
  const totals: RunTotals = {
    tables: rows.length,
    tablesFailed: 0,
    tablesLoadFailed: 0,
    rowsExtracted: 0,
    rowsLoaded: null,
    expectedRows: null,
  };
  for (const row of rows) {
    if (row.status === "failed" || row.load?.status === "failed") totals.tablesFailed += 1;
    if (row.load?.status === "failed") totals.tablesLoadFailed += 1;
    totals.rowsExtracted += Number.isFinite(row.records) ? row.records : 0;
    if (row.load?.rowsLoaded != null) {
      totals.rowsLoaded = (totals.rowsLoaded ?? 0) + row.load.rowsLoaded;
    }
    if (row.load?.expectedRows != null) {
      totals.expectedRows = (totals.expectedRows ?? 0) + row.load.expectedRows;
    }
  }
  return totals;
}

/**
 * True when a load step of the run said it had no destination (`metrics.destination` "none",
 * `runner/steps/loadSnowflake.ts`, the load with no destination). Only then does a skipped
 * table read "no destination".
 */
export function loadHadNoDestination(
  steps: ReadonlyArray<{ step: string; metrics: unknown }> | undefined,
): boolean {
  return (steps ?? []).some(
    (row) =>
      row.step === "load" &&
      row.metrics !== null &&
      typeof row.metrics === "object" &&
      (row.metrics as { destination?: unknown }).destination === "none",
  );
}

/**
 * #1148: the `attempt` of a table's load outcome seen live (`withLiveLoadOutcomes` in
 * `lib/runs/runActivity.ts`), never a step's: the record attaches the counts once the load ends.
 */
export const LIVE_LOAD_ATTEMPT = 0;

/** The run's Tables grids are paged at this many rows (#1465). */
export const ENTITY_PAGE_SIZE = 100;
export const ENTITY_PAGE_SIZE_OPTIONS = [50, 100, 200];

/**
 * #1651: the connections whose load waits for a slot of the instance's import limit (the server's
 * `waitingForSlot` on a pending load step). Their extracted tables read "Waiting to load", never
 * an active "Loading", while nothing loads them.
 */
export function loadsWaitingForSlot(
  steps:
    ReadonlyArray<{ step: string; dataflowId: string | null; waitingForSlot?: true }> | undefined,
): ReadonlySet<string> {
  const waiting = new Set<string>();
  for (const row of steps ?? []) {
    if (row.step === "load" && row.waitingForSlot && row.dataflowId) waiting.add(row.dataflowId);
  }
  return waiting;
}

/**
 * #1672: the connections whose running load is sending its tables' files to the destination (the
 * server's `sendingFilesSince` on a running load step). Their extracted tables read "Sending
 * files", never a "Loading" that no table can reach until the push ends.
 */
export function loadsSendingFiles(
  steps:
    | ReadonlyArray<{
        step: string;
        status?: string;
        dataflowId: string | null;
        sendingFilesSince?: string;
      }>
    | undefined,
): ReadonlySet<string> {
  const sending = new Set<string>();
  for (const row of steps ?? []) {
    if (row.step !== "load" || row.status !== "running") continue;
    if (row.sendingFilesSince && row.dataflowId) sending.add(row.dataflowId);
  }
  return sending;
}

/** #1672: an extracted table whose connection's load is sending the tables' files. */
const SENDING_FILES = "Sending files";

/**
 * The Status filter's words. The filter reads the stage a person sees on the badge, so a table
 * whose extract succeeded and whose load failed is found under "Failed".
 */
export const ENTITY_STAGE_FILTER_OPTIONS = [
  { value: "Failed", label: "Failed" },
  { value: "Loaded", label: "Loaded" },
  { value: "No rows", label: "No rows" },
  { value: "In progress", label: "In progress" },
  { value: "Waiting", label: "Waiting" },
  { value: "Not loaded", label: "Not loaded" },
] as const;

export type EntityStageFilterValue = (typeof ENTITY_STAGE_FILTER_OPTIONS)[number]["value"];

/** A grid row with the filterable stage its badge shows. */
export type EntityStageRow = EntityProgressRow & { stage: EntityStageFilterValue };

/** The coarse stage of the badge `entityStagePresentation` draws, for the Status filter. */
export function entityStageFilterValue(
  row: EntityProgressRow,
  runTerminal: boolean | undefined,
  noDestination = false,
  waitingLoads?: ReadonlySet<string>,
  sendingLoads?: ReadonlySet<string>,
  reportingRows?: ReadonlySet<string>,
): EntityStageFilterValue {
  const presentation = entityStagePresentation(
    row,
    runTerminal,
    noDestination,
    waitingLoads,
    sendingLoads,
    reportingRows,
  );
  if (presentation.color === "danger") return "Failed";
  if (presentation.label === "Loaded") return "Loaded";
  if (presentation.label === NO_ROWS_LABEL || presentation.label === NOT_CREATED_LABEL) {
    return "No rows";
  }
  if (presentation.isActive) return "In progress";
  if (
    presentation.label === "Waiting" ||
    presentation.label === WAITING_TO_LOAD ||
    presentation.label === WAITING_TO_READ
  ) {
    return "Waiting";
  }
  return "Not loaded";
}

/**
 * #2389: the connections whose connector reports rows read while a table is still being read
 * (the periodic `TABLE_PROGRESS`): one of their tables is running with a count. Only for those
 * does a running table with no rows yet read "Waiting to read": a connector that reads many
 * tables at once marks every one started when it queues it. A connector that reports a count
 * only when a table finishes (SQL Server, MySQL, Paylocity) never has such a row, so its running
 * table keeps reading "Extracting", which is what it is doing.
 *
 * This is what the rows show NOW. A grid holds it for the rest of the run
 * (`useReportingConnections`), so a queued table does not flip back to "Extracting" when the
 * tables that were ticking complete. A residual: a table that has started reading but has not
 * sent its first tick yet reads "Waiting to read" for at most one tick interval (60 s), because
 * the connector sends no signal that tells reading from queued.
 */
export function connectionsReportingRows(rows: readonly EntityProgressRow[]): ReadonlySet<string> {
  const reporting = new Set<string>();
  for (const row of rows) {
    if (row.status === "running" && row.records > 0) reporting.add(row.dataflowId);
  }
  return reporting;
}

/** Rows carrying their stage, for a grid whose Status column is `key: "stage"`. */
export function withEntityStage(
  rows: EntityProgressRow[],
  runTerminal: boolean | undefined,
  noDestination = false,
  waitingLoads?: ReadonlySet<string>,
  sendingLoads?: ReadonlySet<string>,
  reportingRows?: ReadonlySet<string>,
): EntityStageRow[] {
  const reporting = reportingRows ?? connectionsReportingRows(rows);
  return rows.map((row) => ({
    ...row,
    stage: entityStageFilterValue(
      row,
      runTerminal,
      noDestination,
      waitingLoads,
      sendingLoads,
      reporting,
    ),
  }));
}

/** #2389: a started table its connector has not read a row of yet, while others are read. */
export const WAITING_TO_READ = "Waiting to read";

/** #1651: an extracted table whose connection's load waits for a slot of the import limit. */
const WAITING_TO_LOAD = "Waiting to load";

/**
 * One badge per table that names the furthest stage it reached: the extract's status combined
 * with the load's outcome. `runTerminal` tells a load that is still to come ("Loading") from one
 * that never ran ("Extracted, not loaded"). Undefined means the caller cannot tell (the run's
 * status is unknown and no table is still extracting): an extracted table without a load then
 * reads a neutral "Extracted", because asserting either "Loading" or "not loaded" could be false.
 * `waitingLoads` (#1651, `loadsWaitingForSlot`) names the connections whose load waits for a slot:
 * their extracted tables read "Waiting to load" while the run is moving. `sendingLoads` (#1672,
 * `loadsSendingFiles`) names the connections whose load is sending the tables' files: their
 * extracted tables read "Sending files". `reportingRows` (#2389, `connectionsReportingRows`) names
 * the connections whose connector reports rows read as it goes: their started tables with no
 * rows yet read "Waiting to read", not "Extracting".
 */
export function entityStagePresentation(
  row: EntityProgressRow,
  runTerminal: boolean | undefined,
  noDestination = false,
  waitingLoads?: ReadonlySet<string>,
  sendingLoads?: ReadonlySet<string>,
  reportingRows?: ReadonlySet<string>,
): StatusPresentation {
  if (row.load) {
    if (row.load.status === "failed") {
      return { label: "Load failed", color: "danger", isActive: false };
    }
    // #2392: a table this import brought no row for is neutral, never the green Loaded.
    const noRows = tableNoRows(row);
    if (noRows === "not_created") {
      return {
        label: NOT_CREATED_LABEL,
        color: "subtle",
        isActive: false,
        title: NOT_CREATED_HOVER,
      };
    }
    if (noRows === "no_rows") {
      return { label: NO_ROWS_LABEL, color: "subtle", isActive: false, title: NO_ROWS_HOVER };
    }
    if (row.load.status === "skipped") {
      // A skip has more than one cause (a later run landed the table, the extractor skipped it,
      // an empty table, no destination). Only a load that said it had no destination
      // (`loadHadNoDestination`) names that cause; any other skip, live or recorded, reads
      // "Not loaded", so the words do not change when the step ends.
      return noDestination
        ? { label: "Extracted, no destination", color: "warning", isActive: false }
        : { label: "Not loaded", color: "warning", isActive: false };
    }
    return { label: "Loaded", color: "success", isActive: false };
  }
  switch (row.status) {
    case "failed":
      return { label: "Extract failed", color: "danger", isActive: false };
    case "skipped":
      return { label: "Skipped by the extractor", color: "warning", isActive: false };
    case "pending":
    case "waiting":
      return runTerminal
        ? { label: "Not extracted", color: "subtle", isActive: false }
        : { label: "Waiting", color: "subtle", isActive: false };
    case "running":
      if (runTerminal) return { label: "Extract stopped", color: "subtle", isActive: false };
      if (row.records === 0 && reportingRows?.has(row.dataflowId)) {
        return { label: WAITING_TO_READ, color: "subtle", isActive: false };
      }
      return { label: "Extracting", color: "informative", isActive: true };
    default:
      if (runTerminal === undefined)
        return { label: "Extracted", color: "subtle", isActive: false };
      if (runTerminal) return { label: "Extracted, not loaded", color: "subtle", isActive: false };
      // #1651: the load is not running yet; it waits for a slot of the import limit.
      if (waitingLoads?.has(row.dataflowId)) {
        return { label: WAITING_TO_LOAD, color: "subtle", isActive: false };
      }
      return sendingLoads?.has(row.dataflowId)
        ? { label: SENDING_FILES, color: "informative", isActive: true }
        : { label: "Loading", color: "informative", isActive: true };
  }
}

/**
 * The Log Analytics query that finds everything this run wrote (FR-56, NFR-6).
 *
 * The workspace URL is deployment-specific and is not part of any build this
 * branch produces, so `logAnalyticsUrl` returns null until one is configured
 * and the screen offers the query itself to paste. A dead link that looks like
 * a working one is worse than no link: it costs a support round trip to learn
 * that it never went anywhere.
 */
export function logAnalyticsQuery(runId: string): string {
  return [
    "AppTraces",
    "| where TimeGenerated > ago(1d)",
    `| where Properties["skydata.run_id"] == "${runId}"`,
    "| order by TimeGenerated asc",
  ].join("\n");
}

/** Configured per deployment; absent in every build this branch produces. */
export function logAnalyticsUrl(runId: string): string | null {
  const configured = (import.meta.env as Record<string, string | undefined>).VITE_LOG_ANALYTICS_URL;
  if (!configured) return null;
  const separator = configured.includes("?") ? "&" : "?";
  return `${configured}${separator}q=${encodeURIComponent(logAnalyticsQuery(runId))}`;
}

/** #986: what a run that has not begun reads in a Started place. */
export const NOT_STARTED_LABEL = "Not started";

/**
 * #986, #1041: a queued run with no start time has not started, and neither has a run cancelled
 * while it was still queued, so its creation time is not a start.
 */
export function isNotStarted(row: { status: string; startedAt?: string | null }): boolean {
  return (row.status === "queued" || row.status === "cancelled") && !row.startedAt;
}

/**
 * #986: the one wording of a run's Started value, for the run page and every run list:
 * "Not started" for a run that never began, else the start (the creation time only when no start exists).
 */
export function startedText(
  row: {
    status: string;
    startedAt?: string | null;
    runStartedAt?: string | null;
    createdAt: string;
  },
  zone: string,
  style: "seconds" | "short" = "seconds",
): string {
  if (isNotStarted(row)) return NOT_STARTED_LABEL;
  return formatInstant(row.startedAt ?? row.runStartedAt ?? row.createdAt, zone, style);
}

/** #982: the server's cancel label as it reads mid-sentence ("Cancelled by the system"). */
export function cancelledByWording(label: string): string {
  if (label === "Removed user") return "a removed user";
  if (label === "System") return "the system";
  return label;
}

/** The text the runner and `cancel` store on a force-cancelled step (retry reads it). */
export const STEP_CANCELLED_TEXT = "Cancelled by an operator";

/**
 * #1014 (PR #1312 review): the status this connection's row reads, from its own part of the run,
 * as the Connections list and the page header read it (`own_outcome`, `forceResetRunStatus`). A
 * run that landed Failed only because ANOTHER connection's step failed (a force reset cancelled
 * this one's steps) reads Cancelled here. Any other run reads its own status.
 */
export function ownRunStatus(row: RunSummaryRow): RunSummaryRow["status"] {
  const steps = row.connection?.steps ?? [];
  if (row.status !== "failed" || steps.length === 0) return row.status;
  if (steps.some((step) => step.status === "failed")) return row.status;
  return steps.some((step) => step.status === "cancelled") ? "cancelled" : row.status;
}

/**
 * #2389: a running table with no rows yet whose connector reports rows as it reads has not been
 * read yet: its Extracted count is a dash, never "0", which would say it was read and was empty.
 */
export function notReadYet(
  row: Pick<EntityProgressRow, "status" | "records" | "dataflowId">,
  reportingRows: ReadonlySet<string> | undefined,
): boolean {
  return (
    row.status === "running" && row.records === 0 && reportingRows?.has(row.dataflowId) === true
  );
}
